import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Container,
  CssBaseline,
  Fab,
  LinearProgress,
  List,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import EditClientAgent from './EditClientAgent';
import ClientAgentItem, { ClientAgent_ItemFragment } from './ClientAgentItem';
import { graphql, FragmentType, useFragment } from '../../../__generated__';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import useGmdUserInfo from '../../../hooks/gmdUserInfo';

export const getTenantAgentsQuery = graphql(/* GraphQL */ `
  query Agents($tenantId: String!) {
    agents(tenantId: $tenantId) {
      ...ClientAgent_ItemFragment
    }
  }
`);

export const deactivateAgentMutation = graphql(/* GraphQL */ `
  mutation DeactivateAgent($agentId: ID!) {
    deactivateAgent(id: $agentId) {
      code
      message
      success
    }
  }
`);

type ClientAgentTabProps = {
  currency: string;
};

const ClientAgentsTab = (props: ClientAgentTabProps) => {
  const { currency } = props;
  let { tenantId } = useParams();
  const { canAddAgent, canEditAgent, canDeleteAgent } = useGmdUserInfo();
  const { data, loading, error, refetch } = useQuery(getTenantAgentsQuery, {
    variables: { tenantId: tenantId || '' },
    skip: (tenantId || '')?.length === 0,
  });
  const [
    deactivateAgent,
    { error: deactivateError, loading: deactivateLoading },
  ] = useMutation(deactivateAgentMutation, {
    refetchQueries: [getTenantAgentsQuery],
  });
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] =
    useState<FragmentType<typeof ClientAgent_ItemFragment>>();
  const agent = useFragment(ClientAgent_ItemFragment, selectedAgent) || null;

  const onCloseEdit = (itemsChanged?: boolean) => {
    setOpenEdit(false);
    if (itemsChanged) {
      refetch();
    }
  };

  const onOpenAdd = () => {
    setOpenAdd(true);
  };

  const onOpenEdit = () => {
    setOpenEdit(true);
  };

  const onCloseAdd = (itemsChanged?: boolean) => {
    setOpenAdd(false);
    if (itemsChanged) {
      refetch();
    }
  };

  const onDeactivate = () => {
    setConfirmDeactivate(true);
  };

  const onDeactivateAgent = async () => {
    setConfirmDeactivate(false);
    if (agent) {
      deactivateAgent({ variables: { agentId: agent.id } });
    }
  };

  if (!tenantId) {
    return <div>Missing platform client id</div>;
  }

  const displayError = error?.message || deactivateError?.message;
  const dataLoading = loading || deactivateLoading;
  const agents = data?.agents || [];

  return (
    <Container component="main">
      <CssBaseline />
      <Box
        sx={{
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {dataLoading && <LinearProgress />}
        {displayError && (
          <Typography variant="caption" gutterBottom color="red">
            {displayError}
          </Typography>
        )}
        {(!agents || agents.length === 0) && <div>No available agents</div>}
        {agents && agents.length > 0 && (
          <>
            <List
              sx={{ width: '100%', minWidth: 330, bgcolor: 'background.paper' }}
            >
              {agents.map((agent, index) => {
                if (agent === null) return null;
                return (
                  <ClientAgentItem
                    tenant={tenantId || ''}
                    key={index}
                    agent={agent}
                    allowEdit={canEditAgent}
                    allowDelete={canDeleteAgent}
                    setSelectedAgent={setSelectedAgent}
                    onEdit={onOpenEdit}
                    onDeactivate={onDeactivate}
                  />
                );
              })}
            </List>
          </>
        )}
        {canAddAgent && (
          <Fab
            sx={{ position: 'fixed', bottom: 50, right: 50 }}
            aria-label={'Add'}
            color={'primary'}
            onClick={onOpenAdd}
            disabled={dataLoading}
          >
            <AddIcon />
          </Fab>
        )}
      </Box>
      {selectedAgent && (
        <EditClientAgent
          open={openEdit}
          tenantId={tenantId}
          currency={currency}
          agent={selectedAgent}
          onClose={onCloseEdit}
        />
      )}
      <EditClientAgent
        open={openAdd}
        tenantId={tenantId}
        currency={currency}
        onClose={onCloseAdd}
      />
      <ConfirmDialog
        open={confirmDeactivate}
        title={'Deactivate Agent'}
        message={'Are you sure you want to deactivate this agent?'}
        onCancel={() => setConfirmDeactivate(false)}
        onOk={onDeactivateAgent}
      />
    </Container>
  );
};

export default ClientAgentsTab;
